import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { TieredMenu } from "primereact/tieredmenu";
import React, { useMemo, useRef } from "react";

type ActionMenuButtonProps = {
  label: string;
  model: MenuItem[];
  disabled?: boolean;
  loading?: boolean;
  size?: "small" | "large";
  severity?: "secondary" | "warning" | "danger" | "info" | "success" | "help" | "contrast";
  rounded?: boolean;
  outlined?: boolean;
  className?: string;
  style?: React.CSSProperties;
  menuStyle?: React.CSSProperties;
  icon?: string;
  badge?: boolean;
  ariaLabel?: string;
  tiered?: boolean;
  dataTour?: string;
};

const ActionMenuButton = ({
  label,
  model,
  disabled = false,
  loading = false,
  size,
  severity,
  rounded = false,
  outlined = false,
  className,
  style,
  menuStyle,
  icon = "pi pi-chevron-down",
  badge = false,
  ariaLabel,
  tiered = false,
  dataTour,
}: ActionMenuButtonProps) => {
  const menuRef = useRef<any>(null);
  const enabledItems = useMemo(
    () => (Array.isArray(model) ? model.filter((item) => item && item.visible !== false) : []),
    [model],
  );
  const isDisabled = disabled || loading || enabledItems.length === 0;

  return (
    <>
      {tiered ? (
        <TieredMenu model={enabledItems} popup ref={menuRef} style={menuStyle} />
      ) : (
        <Menu model={enabledItems} popup ref={menuRef} style={menuStyle} />
      )}
      {badge ? (
        <span
          className={`p-tag p-component p-tag-${severity || "secondary"}`}
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          aria-haspopup="menu"
          aria-disabled={isDisabled}
          onClick={(event) => { if (!isDisabled) menuRef.current?.toggle(event as any); }}
          onKeyDown={(event) => {
            if (!isDisabled && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              menuRef.current?.toggle(event as any);
            }
          }}
          style={{ cursor: isDisabled ? "default" : "pointer", opacity: isDisabled ? 0.65 : 1, ...style }}
        >
          <span className="p-tag-value">{label}</span><i className={`${icon} ml-2`} />
        </span>
      ) : <Button
        type="button"
        label={label}
        icon={icon}
        iconPos="right"
        size={size}
        severity={severity}
        rounded={rounded}
        outlined={outlined}
        className={className}
        style={style}
        disabled={isDisabled}
        loading={loading}
        aria-haspopup="menu"
        aria-label={ariaLabel || label}
        aria-expanded={undefined}
        data-tour={dataTour}
        onClick={(event) => menuRef.current?.toggle(event)}
      />}
    </>
  );
};

export default ActionMenuButton;
